// Tiện ích dùng chung cho các màn POS: định dạng, quyền, in hóa đơn, xuất Excel, nhãn trạng thái
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import useAuthStore from 'store/authStore';
import { BRANCHES, METHOD_LABEL, branchName, staffName, type Order, type OrderStatus, type PosRole, type RefundStatus, type Transaction } from './data';
import usePosStore from './store';

export const BRAND = { primary: '#3e91ab', button: '#357f96', dark: '#2c6b7f', soft: '#ecf6f9' };

export const money = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n || 0));
export const moneyShort = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2).replace('.', ',')} tỷ`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1).replace('.', ',')} tr`;
  return money(n);
};
export const fmtDateTime = (iso: string) => dayjs(iso).format('DD/MM/YYYY HH:mm');
export const fmtTime = (iso: string) => dayjs(iso).format('HH:mm');
export const formatPhone = (p: string) => (p && p.length === 10 ? `${p.slice(0, 4)} ${p.slice(4, 7)} ${p.slice(7)}` : p);
export const VN_MOBILE_REGEX = /^0[35789]\d{8}$/;

export const currentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.fullname || u.name || (u.email ? String(u.email).split('@')[0] : '') || 'Thu ngân';
  } catch {
    return 'Thu ngân';
  }
};

type PosAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'import' | 'export' | 'print';
const ROLE_ACTIONS: Record<PosRole, PosAction[]> = {
  cashier: ['view', 'create', 'print'],
  manager: ['view', 'create', 'edit', 'delete', 'approve', 'export', 'print'],
  admin: ['view', 'create', 'edit', 'delete', 'approve', 'import', 'export', 'print'],
};

/**
 * Quyền POS của người đang đăng nhập. Vai trò suy ra từ quyền: super admin → admin,
 * có pos.approve → quản lý, còn lại → thu ngân. Môi trường dev cho phép giả lập vai trò để thử luồng.
 */
export const usePosAuth = () => {
  const { isSuperAdmin, permissions } = useAuthStore();
  const demoRole = usePosStore(s => s.demoRole);
  const realRole: PosRole = isSuperAdmin ? 'admin' : permissions.includes('pos.approve') ? 'manager' : 'cashier';
  const role = demoRole || realRole;
  const can = (action: PosAction) =>
    demoRole ? ROLE_ACTIONS[demoRole].includes(action) : isSuperAdmin || permissions.includes(`pos.${action}`);
  return { role, realRole, can, userName: currentUserName(), isDemo: !!demoRole };
};

export const ORDER_STATUS: Record<OrderStatus, { label: string; cls: string }> = {
  completed: { label: 'Hoàn thành', cls: 'bg-green-50 text-green-700 border-green-200' },
  debt: { label: 'Còn nợ', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  partially_refunded: { label: 'Hoàn một phần', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  refunded: { label: 'Đã hoàn tiền', cls: 'bg-violet-600 text-white border-violet-600' },
  cancelled: { label: 'Đã hủy', cls: 'bg-red-50 text-red-600 border-red-200' },
};

export const REFUND_STATUS: Record<RefundStatus, { label: string; cls: string }> = {
  pending: { label: 'Chờ duyệt', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Đã duyệt – chờ chi', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  paid: { label: 'Đã chi', cls: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Từ chối', cls: 'bg-red-50 text-red-600 border-red-200' },
};

export const StatusPill = ({ label, cls }: { label: string; cls: string }) => (
  <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
);

// ---------------------------------------------------------------------------
// In hóa đơn K80 / A4 qua iframe ẩn (không mở tab mới)

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export function printReceipt(
  order: Order,
  size: 'K80' | 'A4',
  /** Đơn lấy từ API: truyền giao dịch + tên chi nhánh vì không có trong kho dữ liệu máy */
  opts: { change?: number; transactions?: Transaction[]; branchName?: string; reprint?: boolean } = {},
) {
  const store = usePosStore.getState();
  const reprint = opts.reprint ?? order.printCount > 0;
  const txns = (opts.transactions || store.transactions.filter(t => t.orderId === order.id)).filter(t => t.kind !== 'refund');
  const branch = opts.branchName !== undefined ? { name: opts.branchName } : BRANCHES.find(b => b.id === order.branchId);
  const t = order.totals;
  const row = (label: string, value: string, strong = false) =>
    `<tr${strong ? ' class="strong"' : ''}><td>${label}</td><td class="r">${value}</td></tr>`;
  const lines = order.lines.map(l => `
    <tr><td colspan="2" class="name">${esc(l.name)}${l.staffName || staffName(l.staffId) ? ` <span class="muted">(TV: ${esc(l.staffName || staffName(l.staffId))})</span>` : ''}</td></tr>
    <tr><td class="muted">${l.qty} x ${money(l.listPrice)}${l.promoAmount ? ` · KM -${money(l.promoAmount)}` : ''}${l.lineDiscountAmount ? ` · CK -${money(l.lineDiscountAmount)}` : ''}</td><td class="r">${money(l.afterLine)}</td></tr>`).join('');
  const css = size === 'K80'
    ? '@page{size:80mm auto;margin:3mm} body{width:74mm;font-size:12px}'
    : '@page{size:A4;margin:16mm} body{font-size:13px;max-width:720px;margin:0 auto}';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${order.id}</title><style>
    ${css}
    body{font-family:Arial,Helvetica,sans-serif;color:#111}
    h1{font-size:16px;margin:0;text-align:center} .c{text-align:center} .muted{color:#555;font-size:11px}
    table{width:100%;border-collapse:collapse} td{padding:2px 0;vertical-align:top} .r{text-align:right;white-space:nowrap}
    .name{font-weight:600;padding-top:6px} hr{border:0;border-top:1px dashed #999;margin:6px 0}
    .strong td{font-weight:700;font-size:1.15em} .reprint{border:1px solid #111;text-align:center;font-weight:700;margin:4px 0;padding:2px}
  </style></head><body>
    <h1>KHOTHI</h1>
    <div class="c muted">${esc(branch?.name || '')}</div>
    <div class="c" style="font-weight:700;margin-top:6px">HÓA ĐƠN BÁN HÀNG</div>
    ${reprint ? '<div class="reprint">BẢN IN LẠI</div>' : ''}
    <table>
      ${row('Mã đơn', order.id)}
      ${row('Thời gian', fmtDateTime(order.createdAt))}
      ${row('Thu ngân', esc(order.cashier))}
      ${row('Khách hàng', esc(order.customerName) + (order.customerPhone ? ` · ${formatPhone(order.customerPhone)}` : ''))}
    </table><hr/>
    <table>${lines}</table><hr/>
    <table>
      ${row('Tạm tính', money(t.subtotal))}
      ${t.itemPromo ? row('Khuyến mãi', '-' + money(t.itemPromo)) : ''}
      ${t.lineDiscount ? row('Chiết khấu dòng', '-' + money(t.lineDiscount)) : ''}
      ${t.orderPromo ? row('KM đơn', '-' + money(t.orderPromo)) : ''}
      ${t.orderDiscount ? row('Chiết khấu đơn', '-' + money(t.orderDiscount)) : ''}
      ${t.voucher ? row(`Voucher ${esc(order.voucherCode)}`, '-' + money(t.voucher)) : ''}
      ${t.rounding ? row('Làm tròn', money(t.rounding)) : ''}
      ${row('TỔNG CỘNG', money(t.total) + 'đ', true)}
    </table><hr/>
    <table>
      ${txns.map(x => row(METHOD_LABEL[x.method] + (x.txnRef ? ` (${x.txnRef})` : ''), money(x.amount))).join('')}
      ${opts.change ? row('Tiền thối', money(opts.change)) : ''}
      ${order.debt ? row('Còn nợ', money(order.debt), true) : ''}
    </table>
    ${order.note ? `<hr/><div class="muted">Ghi chú: ${esc(order.note)}</div>` : ''}
    <hr/><div class="c muted">Cảm ơn quý khách và hẹn gặp lại!</div>
  </body></html>`;

  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const doc = frame.contentWindow!.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow!.focus();
    frame.contentWindow!.print();
    setTimeout(() => frame.remove(), 1000);
  }, 200);
  if (!opts.transactions) store.markPrinted(order.id, currentUserName(), size);
}

// ---------------------------------------------------------------------------

export function exportExcel(fileName: string, sheetName: string, rows: Record<string, string | number>[]) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
}

export const branchOptions = BRANCHES.map(b => ({ value: b.id, label: b.name }));
export { branchName };

/** Bỏ dấu tiếng Việt để tìm kiếm: "Đà Nẵng" → "da nang" */
export const foldVi = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();
/** filterOption cho antd Select có showSearch: so khớp label không phân biệt dấu / hoa thường */
export const viFilterOption = (input: string, option?: { label?: unknown }) =>
  foldVi(String(option?.label ?? '')).includes(foldVi(input.trim()));

// ---------------------------------------------------------------------------
// Đơn nháp: khóa tự hết hạn sau 15 phút (D1), hết hạn sau draftTtlDays ngày (D2)

export const DRAFT_LOCK_MINUTES = 15;

export const draftLockInfo = (d: { lockedBy: string | null; lockedAt: string | null }, me: string) => {
  if (!d.lockedBy || !d.lockedAt) return { locked: false as const };
  const ageMin = (Date.now() - new Date(d.lockedAt).getTime()) / 60000;
  if (d.lockedBy === me) return { locked: false as const, mine: true };
  if (ageMin >= DRAFT_LOCK_MINUTES) return { locked: false as const, stale: true, by: d.lockedBy };
  return { locked: true as const, by: d.lockedBy, minutes: Math.floor(ageMin) };
};

export const draftDaysLeft = (updatedAt: string, ttlDays: number) =>
  ttlDays - (Date.now() - new Date(updatedAt).getTime()) / 86400000;

export const formatDaysLeft = (days: number) => {
  if (days <= 0) return 'Đã hết hạn';
  if (days < 1) return `Hết hạn sau ${Math.max(1, Math.round(days * 24))} giờ`;
  return `Hết hạn sau ${Math.floor(days)} ngày`;
};
